/**
 * Story canvas grid units at the API boundary.
 *
 * The canvas works on `GRID_COLUMNS` (24) columns. The server holds every
 * position on a grid of its own, named by the position's `cols` — absent
 * means 12, the grid every layout was stored on so far. Reads map a stored
 * x / w onto the canvas grid; writes map them back onto the server's grid.
 * Rows (y / h) are the same unit on both sides and pass through unchanged.
 *
 * 12 → 24 is exact: x and w double and land on the same pixels, so a stored
 * layout keeps its look. While the server's grid is 12 the canvas snaps x / w
 * to even columns (`columnStep`), so what is placed is exactly what is saved;
 * once the server holds 24 columns the full 24-column snap applies.
 */
import { GRID_COLUMNS } from "../dataStories/generateStory.types";
import type { WidgetPosition } from "../dataStories/generateStory.types";
import type { ItemPositionApi } from "./storyCanvas.types";

/** The grid of every position stored before `cols` existed. */
export const LEGACY_STORED_GRID_COLUMNS = 12;

export interface GridGeometry {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** pos_* fields of an add / move request, on the server's grid. */
export interface StoredPlacement {
  pos_x: number;
  pos_y: number;
  pos_w: number;
  pos_h: number;
  pos_cols?: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** A usable column count, else the legacy 12. */
export function toStoredColumns(cols: number | null | undefined): number {
  return typeof cols === "number" && Number.isInteger(cols) && cols > 0
    ? cols
    : LEGACY_STORED_GRID_COLUMNS;
}

/** A stored position on the canvas grid, remembering the grid it came from. */
export function toCanvasPosition(position: ItemPositionApi): WidgetPosition {
  const cols = toStoredColumns(position.cols);
  const w = clamp(Math.round((position.w * GRID_COLUMNS) / cols), 1, GRID_COLUMNS);
  const x = clamp(Math.round((position.x * GRID_COLUMNS) / cols), 0, GRID_COLUMNS - w);
  return {
    x,
    y: position.y,
    w,
    h: position.h,
    storedColumns: cols,
    ...(typeof position.h_fixed === "boolean"
      ? { heightFixed: position.h_fixed }
      : {}),
  };
}

/** Canvas geometry on a server grid of `cols` columns. */
export function toStoredGeometry(
  geometry: GridGeometry,
  cols: number,
): GridGeometry {
  const stored = toStoredColumns(cols);
  const w = clamp(Math.round((geometry.w * stored) / GRID_COLUMNS), 1, stored);
  const x = clamp(Math.round((geometry.x * stored) / GRID_COLUMNS), 0, stored - w);
  return { x, y: geometry.y, w, h: geometry.h };
}

/**
 * The pos_* fields for canvas geometry. `pos_cols` is sent only off the
 * legacy grid, so a server that predates it sees the request it always did.
 */
export function toStoredPlacement(
  geometry: GridGeometry,
  cols: number,
): StoredPlacement {
  const stored = toStoredColumns(cols);
  const { x, y, w, h } = toStoredGeometry(geometry, stored);
  return {
    pos_x: x,
    pos_y: y,
    pos_w: w,
    pos_h: h,
    ...(stored === LEGACY_STORED_GRID_COLUMNS ? {} : { pos_cols: stored }),
  };
}

/** A move / resize on the server's grid: w / h stay absent when absent. */
export interface StoredPositionUpdate {
  x: number;
  y: number;
  w?: number;
  h?: number;
  /** Absent on the legacy 12, which a server before `cols` assumes. */
  cols?: number;
}

/** A canvas move / resize (w / h optional) on a server grid of `cols`. */
export function toStoredPositionUpdate(
  update: { x: number; y: number; w?: number; h?: number },
  cols: number,
): StoredPositionUpdate {
  const stored = toStoredColumns(cols);
  const w =
    update.w == null
      ? undefined
      : clamp(Math.round((update.w * stored) / GRID_COLUMNS), 1, stored);
  const x = clamp(
    Math.round((update.x * stored) / GRID_COLUMNS),
    0,
    stored - (w ?? 1),
  );
  return {
    x,
    y: update.y,
    ...(w == null ? {} : { w }),
    ...(update.h == null ? {} : { h: update.h }),
    ...(stored === LEGACY_STORED_GRID_COLUMNS ? {} : { cols: stored }),
  };
}

/**
 * The grid the server stores this canvas on: the finest grid any of its
 * positions came from, else the legacy 12. A server that holds a finer grid
 * reports it on its positions; until then writes stay on 12.
 */
export function storedGridColumnsOf(
  positions: ReadonlyArray<WidgetPosition | null | undefined>,
): number {
  return positions.reduce<number>(
    (finest, position) =>
      position?.storedColumns != null
        ? Math.max(finest, toStoredColumns(position.storedColumns))
        : finest,
    LEGACY_STORED_GRID_COLUMNS,
  );
}

/** Canvas columns per stored column: x / w snap to multiples of this. */
export function columnStep(storedColumns: number): number {
  return Math.max(1, Math.round(GRID_COLUMNS / toStoredColumns(storedColumns)));
}
